import {
  FormData,
  FormFieldValue,
  RefKeyValueRegistry,
  FormDataConverter,
  FormSchema,
  isInputClassElement,
} from "@graffer-inc/form-schema";
import { BaseEntity } from "../base/base-entity";
import { MimeType } from "../procedure/mime-types";
import {
  ApplicationContent,
  ApplicationContentType,
} from "./application-content";
import { PaymentType } from "../..";
import { BaseTypeLiterals } from "../base/base-type";
import { convertToFormattedId } from "../../utils/id-formatter";
import type { ApplicantSchemaKeys } from "../procedure/procedure-detail";

export const ApplyStatusList = [
  "UNTREATED",
  "WIP",
  "CLOSED_COMPLETED",
  "CLOSED_WITHDRAWN",
  "CLOSED_REJECTED",
] as const;
export type ApplyStatus = (typeof ApplyStatusList)[number];
export type SignatureStatus = "IS_PENDING" | "IS_VALID" | "IS_INVALID";
export type JpkiGenderLiterals = "1" | "2" | "3";
export type TenantProcessResultDocument = {
  [key: string]: Extract<FormFieldValue, string | undefined>;
};

export const UserAccessMethod = {
  NONE: "NONE",
  LOGIN: "LOGIN",
  EMAIL_AUTH: "EMAIL_AUTH",
} as const;
export type UserAccessMethodType =
  (typeof UserAccessMethod)[keyof typeof UserAccessMethod];

export type ApplicationType = ApplicationContentType & {
  readonly id: string;
  readonly version: number;
  readonly procedureId: string;
  readonly procedureVersion: number;
  readonly name: string;
  status: ApplyStatus;
  readonly issueFiles: Array<IssueFile>;
  readonly paymentsV2?: Array<PaymentType>;
  readonly signature?: Signatures;
  readonly userIdExists: boolean;
  readonly messages: Array<{
    content: string;
    createdAt: Date;
  }>;
  readonly internalComments: Array<{
    content: string;
    createdAt: Date;
  }>;
  readonly tenantProcessResult?: {
    readonly document: TenantProcessResultDocument;
  };
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdAtUnixSecond: number;
  readonly updatedAtUnixSecond: number;
  readonly orgId: string;
};

export class ApplicationEntity extends BaseEntity {
  public readonly id: string;
  public readonly version: number;
  public readonly procedureId: string;
  public readonly procedureVersion: number;
  public readonly name: string;
  public readonly jsonDocument: FormData;
  public readonly userIdExists: boolean;
  public status: ApplyStatus;
  public files!: Array<AttachFileWithURL>;
  public myNumbers?: MyNumberWithID[];
  public modifiedAt?: Date;
  public readonly issueFiles: Array<IssueFile>;
  public readonly paymentsV2?: Array<PaymentType>;
  public readonly signature?: Signatures;
  public readonly createdAt: string;
  public readonly updatedAt: string;
  public readonly createdAtDate: Date;
  public readonly updatedAtDate: Date;
  public readonly orgId: string;
  public readonly messages: Array<{
    content: string;
    createdAt: Date;
  }>;
  public readonly internalComments: Array<{
    content: string;
    createdAt: Date;
  }>;
  public readonly tenantProcessResult?: {
    readonly document: TenantProcessResultDocument;
  };

  constructor(values: ApplicationType) {
    super();
    this.id = values.id;
    this.version = values.version;
    this.procedureId = values.procedureId;
    this.procedureVersion = values.procedureVersion;
    this.name = values.name;
    this.jsonDocument = values.jsonDocument;
    this.userIdExists = values.userIdExists;
    this.status = values.status;
    this.files = values.files;
    this.issueFiles = values.issueFiles;
    this.paymentsV2 = values.paymentsV2;
    this.signature = values.signature;
    this.createdAt = values.createdAt;
    this.updatedAt = values.updatedAt;
    this.createdAtDate = new Date(values.createdAtUnixSecond * 1000);
    this.updatedAtDate = new Date(values.updatedAtUnixSecond * 1000);
    this.messages = values.messages;
    this.internalComments = values.internalComments;
    this.tenantProcessResult = values.tenantProcessResult;
    this.orgId = values.orgId;
    this.myNumbers = values.myNumbers;
    this.modifiedAt = values.modifiedAt;
  }

  get formattedId() {
    return convertToFormattedId(this.id);
  }

  /**
   * @deprecated
   * use `getApplicant` instead
   */
  get applicant() {
    return this.getContent().applicant;
  }

  getApplicant(applicantSchemaKeys: ApplicantSchemaKeys) {
    return this.getContent().getApplicant(applicantSchemaKeys);
  }

  /**
   * @deprecated
   * applicant 相当のデータを抜いた FormData を返す。
   * 過去の個別プランでの利用のみを前提としており、利用範囲を増やさないこと。
   * FormData 全体を返す rawDocument, jsonDocument を代わりに使うこと。
   */
  get document() {
    return this.getContent().filteredDocument;
  }

  get rawDocument() {
    return this.getContent().rawDocument;
  }

  get refKeys() {
    return this.getContent().refKeys;
  }

  get refkeyValueRegistry(): RefKeyValueRegistry {
    return this.getContent().refkeyValueRegistry;
  }

  /**
   * 参照キーを有していないファイルを取得する。
   * 個別カスタム手続きで、汎用フォーム設定内でのファイル設定がないケースで利用される
   */
  get refKeyLessFiles() {
    return this.files.filter((f) => !f.refKey);
  }

  getUserAccessMethod(
    applicantSchemaKeys: ApplicantSchemaKeys,
  ): UserAccessMethodType {
    if (this.userIdExists) {
      return UserAccessMethod.LOGIN;
    }
    if (!!this.getApplicant(applicantSchemaKeys).email) {
      return UserAccessMethod.EMAIL_AUTH;
    }
    return UserAccessMethod.NONE;
  }

  hasRefKeylessFiles() {
    return this.refKeyLessFiles.length > 0;
  }

  hasAttachedFiles(formSchema: FormSchema) {
    if (this.files.length === 0) {
      return false;
    }
    const formDataMap = new FormDataConverter(
      formSchema,
      this.jsonDocument,
    ).flattenWithElement();
    const flatten = FormSchema.fromJSON(formSchema)
      .flattenDeep()
      .filter(isInputClassElement);
    return flatten.some((elem) =>
      this.files.some(
        (file) =>
          formDataMap[elem.formFieldId] &&
          formDataMap[elem.formFieldId].value === file.refKey,
      ),
    );
  }

  getPaymentsV2(): PaymentType[] {
    return this.paymentsV2 || [];
  }

  hasAlertingPayment(): boolean {
    return this.getPaymentsV2().some(
      (p) => p.status == "exceeding_payment" || p.status == "deficient_payment",
    );
  }

  noPaymentRequired() {
    return this.getPaymentsV2().length === 0;
  }

  getContent() {
    return new ApplicationContent(this);
  }

  hasSignature() {
    return !!this.signature;
  }

  matchSignatureVerifyStatus<R>(matcher: BaseTypeLiterals<SignatureStatus, R>) {
    const signatureStatus: SignatureStatus = this.signatureStatus();
    return matcher[signatureStatus];
  }

  signatureStatus(): SignatureStatus {
    if (!this.hasSignature()) {
      throw new Error("the application does not have signature");
    }
    if (
      this.signature!.certCheckResult.isValid === undefined ||
      this.signature!.certCheckResult.isValid === null
    ) {
      return "IS_PENDING";
    }
    if (this.signature!.certCheckResult.isValid) {
      return "IS_VALID";
    } else {
      return "IS_INVALID";
    }
  }
}

export type AttachFile = {
  refKey?: string;
  name: string;
  size: number;
  type: MimeType;
  tags?: string[];
};

export type AttachFileWithData = AttachFile & {
  data: string;
};
export type AttachFileWithURL = AttachFile & {
  url: string;
};
export type ModifiedFile = Partial<AttachFileWithURL> &
  AttachFile & {
    preUploadFileId?: string;
  };

export type MyNumber = {
  refKey: string;
};
export type MyNumberWithID = MyNumber & {
  myNumberId: string;
  last4Digit?: string;
};
export type ModifiedMyNumber = Partial<MyNumberWithID> &
  MyNumber & {
    myNumber?: string;
  };

export type IssueFile = {
  type: string;
  name: string;
  url: string;
  isDeleted: boolean;
  isLgpkiSigned?: boolean;
  isDownloaded: boolean;
};

export type PaymentInfo = {
  requireAmount: number;
  paidAmount: number;
  creditCardResult?: {
    cardId: string;
    last4: string;
    brand: string;
    stripeChargeId?: string;
  };
};

export type Signatures = {
  jpkiSubjectAltName?: {
    commonName: string;
    address: string;
    gender: JpkiGenderLiterals;
    dateOfBirth: string;
  };
  certificateType: CertificateType;
  certCheckResult: {
    isValid?: boolean;
  };
};

export type CertificateType = "JPKI_SIG_CERT" | "MOJ_CERT";

export type PreUploadFile = {
  type: string;
  data: string;
};

export type PreUploadFileFromFile = {
  type: string;
  data: File;
};

// 管理画面用のステータステキスト。
// kintone 連携機能でも利用するため、安易に変更しないこと。
export const statusTextForConsole = (status: ApplyStatus): string => {
  switch (status) {
    case "UNTREATED":
      return "未処理";
    case "WIP":
      return "処理中";
    case "CLOSED_COMPLETED":
      return "完了";
    case "CLOSED_REJECTED":
      return "差し戻し";
    case "CLOSED_WITHDRAWN":
      return "取下げ";
  }
};

// 管理画面用のステータステキスト。
// kintone 連携機能でも利用するため、安易に変更しないこと。
export const statusFromText = (statusText: string): ApplyStatus | undefined => {
  switch (statusText) {
    case "未処理":
      return "UNTREATED";
    case "処理中":
      return "WIP";
    case "完了":
      return "CLOSED_COMPLETED";
    case "差し戻し":
      return "CLOSED_REJECTED";
    case "取下げ":
      return "CLOSED_WITHDRAWN";
    default:
      return undefined;
  }
};
